import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { NgClass } from '@angular/common';

export interface SelectOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-select',
  imports: [NgClass],
  templateUrl: './select.html',
})
export class SelectComponent implements OnInit, OnDestroy {
  options = input<SelectOption[]>([]);
  placeholder = input<string>('Select...');
  value = input<string>('');
  error = input<boolean>(false);
  loading = input<boolean>(false);
  hasMore = input<boolean>(false);
  borderless = input<boolean>(false);
  searchable = input<boolean>(false);
  /** The parent filters `options` itself (server search via `searchChange`); skip local filtering. */
  remoteSearch = input<boolean>(false);
  /** Show options in the given order instead of alphabetically (e.g. admin-defined list order). */
  keepOrder = input<boolean>(false);
  changed = output<string>();
  loadMore = output<void>();
  searchChange = output<string>();

  protected searchTerm = signal('');
  private static readonly DROPDOWN_HEIGHT = 240;
  private static openEvent$ = new Subject<number>();
  private static _counter = 0;
  private instanceId = ++SelectComponent._counter;
  private destroyRef = inject(DestroyRef);

  protected open = signal(false);
  protected dropdownTop = signal(0);
  protected dropdownBottom = signal(0);
  protected dropdownLeft = signal(0);
  protected dropdownWidth = signal(0);
  protected openUpward = signal(false);

  protected selectedLabel = computed(
    () => this.options().find((o) => o.value === this.value())?.label ?? '',
  );

  protected sortedOptions = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const visible =
      term && !this.remoteSearch()
        ? this.options().filter((o) => o.label.toLowerCase().includes(term))
        : this.options();
    if (this.keepOrder()) return visible;
    // The empty-value option ("All") stays on top; the rest is alphabetical.
    return [...visible].sort(
      (a, b) => Number(a.value !== '') - Number(b.value !== '') || a.label.localeCompare(b.label),
    );
  });

  constructor(private el: ElementRef) {}

  private computePosition(): void {
    const rect = (this.el.nativeElement as HTMLElement).getBoundingClientRect();
    const h = SelectComponent.DROPDOWN_HEIGHT;
    const spaceBelow = window.innerHeight - rect.bottom;
    const up = spaceBelow < h && rect.top > spaceBelow;
    this.openUpward.set(up);
    this.dropdownLeft.set(rect.left);
    this.dropdownWidth.set(rect.width);
    if (up) {
      this.dropdownBottom.set(Math.max(8, window.innerHeight - rect.top + 4));
    } else {
      this.dropdownTop.set(Math.min(rect.bottom + 4, window.innerHeight - h - 8));
    }
  }

  private reposition = (): void => {
    if (!this.open()) return;
    this.computePosition();
  };

  ngOnInit(): void {
    window.addEventListener('scroll', this.reposition, true);
    SelectComponent.openEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      if (id !== this.instanceId) this.open.set(false);
    });
    // Capture phase: modals stop click propagation, so a bubbling document listener never fires.
    document.addEventListener('pointerdown', this.onOutsidePointerDown, true);
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.reposition, true);
    document.removeEventListener('pointerdown', this.onOutsidePointerDown, true);
  }

  private onOutsidePointerDown = (e: PointerEvent): void => {
    if (this.open() && !this.el.nativeElement.contains(e.target as Node)) this.open.set(false);
  };

  protected toggle(): void {
    if (!this.open()) {
      this.computePosition();
      if (this.searchable() && this.searchTerm()) {
        this.searchTerm.set('');
        // A remote list is still filtered by the previous term: let the parent reset it.
        if (this.remoteSearch()) this.searchChange.emit('');
      }
      SelectComponent.openEvent$.next(this.instanceId);
    }
    this.open.update((v) => !v);
  }

  protected select(value: string): void {
    this.changed.emit(value);
    this.open.set(false);
  }

  protected onSearchInput(term: string): void {
    this.searchTerm.set(term);
    this.searchChange.emit(term);
  }

  protected onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (
      el.scrollHeight - el.scrollTop - el.clientHeight < 60 &&
      this.hasMore() &&
      !this.loading()
    ) {
      this.loadMore.emit();
    }
  }
}
